import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { useAuth, API } from "../App";
import axios from "axios";
import { 
  ArrowRight,
  Shield,
  Users,
  Menu,
  X,
  Target,
  Eye,
  Zap,
  FileCheck,
  Lock,
  TrendingUp,
  CheckCircle2
} from "lucide-react";
import AnimatedLogo from "../components/AnimatedLogo";

export default function AboutUs() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState({
    total_placements: 1497,
    total_colleges: 99,
    avg_ctc: 2400000
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      }
    };
    fetchStats();
  }, []);

  const formatAvgCTC = (ctc) => {
    return `₹${(ctc / 100000).toFixed(0)}L`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 px-4 pt-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between h-14 px-5 bg-background/80 backdrop-blur-xl border border-border/40 rounded-full shadow-sm">
            <AnimatedLogo />
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Home
              </Link>
              <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Rankings
              </Link>
              {user ? (
                <Link to="/profile">
                  <Button variant="ghost" size="sm" className="rounded-full">Profile</Button>
                </Link>
              ) : (
                <Button onClick={() => login('/submit')} size="sm" className="rounded-full px-5 bg-foreground text-background hover:bg-foreground/90">
                  Sign in
                </Button>
              )}
            </div>

            <button 
              className="p-2 md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden mx-4 mt-2 p-4 bg-white dark:bg-zinc-900 border border-border/40 rounded-2xl shadow-lg space-y-2"
        >
          <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-2.5 text-sm rounded-xl hover:bg-secondary transition-colors">
            Home
          </Link>
          <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-2.5 text-sm rounded-xl hover:bg-secondary transition-colors">
            Rankings
          </Link>
          {user ? (
            <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-2.5 text-sm rounded-xl hover:bg-secondary transition-colors">
              Profile
            </Link>
          ) : (
            <Button onClick={() => { setMobileMenuOpen(false); login('/submit'); }} className="w-full mt-2 rounded-xl bg-foreground text-background hover:bg-foreground/90">
              Sign in
            </Button>
          )}
        </motion.div>
      )}

      {/* Hero - Clean & Monochrome */}
      <section className="pt-20 pb-16 sm:pt-28 sm:pb-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-muted-foreground text-sm font-medium mb-4 uppercase tracking-wider">
              About Us
            </p>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-tight">
              We're building trust in placement data
            </h1>
            
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto">
              Independent verification experts bringing transparency to college placement statistics across India.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats - Monochrome */}
      <section className="pb-16 sm:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
            {[
              { value: `${stats.total_placements}+`, label: "Verified Reports" },
              { value: `${stats.total_colleges}+`, label: "Colleges" },
              { value: formatAvgCTC(stats.avg_ctc), label: "Avg Package" },
              { value: "100%", label: "Human Verified" }
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center py-6 px-4 rounded-2xl border border-border/50 bg-secondary/30"
              >
                <div className="text-2xl sm:text-3xl font-bold">{stat.value}</div>
                <div className="text-xs sm:text-sm text-muted-foreground mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision - Monochrome */}
      <section className="pb-16 sm:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 sm:p-8 rounded-2xl border border-border/50 bg-secondary/20"
            >
              <Target className="w-8 h-8 mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold mb-3">Our Mission</h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                Empowering students with verified, accurate salary data. Every data point goes through 
                our rigorous multi-layer verification process.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-6 sm:p-8 rounded-2xl border border-border/50 bg-secondary/20"
            >
              <Eye className="w-8 h-8 mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold mb-3">Our Vision</h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                A future where every student makes informed education decisions based on verified 
                outcomes, not marketing claims.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values - Monochrome */}
      <section className="pb-16 sm:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-xl sm:text-2xl font-semibold text-center mb-8 sm:mb-10"
          >
            What we stand for
          </motion.h2>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: Eye, title: "Transparency" },
              { icon: Shield, title: "Integrity" },
              { icon: Users, title: "Student-First" },
              { icon: Zap, title: "Innovation" }
            ].map((value, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-4 sm:p-5 rounded-xl border border-border/50 bg-secondary/20 hover:bg-secondary/40 transition-colors"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-secondary flex items-center justify-center mx-auto mb-3">
                  <value.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <h3 className="text-sm sm:text-base font-medium">{value.title}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Trust Us - Monochrome */}
      <section className="pb-16 sm:pb-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-xl sm:text-2xl font-semibold text-center mb-8 sm:mb-10"
          >
            Why trust us
          </motion.h2>

          <div className="space-y-3 sm:space-y-4">
            {[
              { icon: Lock, title: "No Paid Rankings", desc: "We don't accept money to boost college rankings" },
              { icon: FileCheck, title: "4-Layer Verification", desc: "Every submission verified before publishing" },
              { icon: TrendingUp, title: "Real Outcomes", desc: "We track actual career outcomes, not just offers" }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-4 p-4 sm:p-5 rounded-xl border border-border/50 bg-secondary/20 hover:bg-secondary/40 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-medium text-sm sm:text-base">{item.title}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - Monochrome */}
      <section className="pb-20 sm:pb-28">
        <div className="max-w-xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-xl sm:text-2xl font-semibold mb-3">
              Join our mission
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground mb-6">
              Your verified submission helps thousands of students make better decisions.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <Button 
                onClick={() => user ? navigate('/submit') : login('/submit')}
                className="rounded-full px-6 bg-foreground text-background hover:bg-foreground/90"
              >
                Submit Placement
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/dashboard')}
                className="rounded-full px-6"
              >
                View Rankings
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer - Minimal */}
      <footer className="border-t border-border/50 py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-center">
            <span className="text-sm font-medium">Transplacement</span>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <span>© 2025</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
